/*
 * <product-form>: variant selection, gallery by color, quantity, add to cart (AJAX, opens the cart
 * drawer via the cart:added event), dynamic checkout lock until a size is chosen, sticky add-to-cart.
 */
(() => {
  'use strict';
  const routes = window.routes || {};

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;
      this.variants = JSON.parse(this.querySelector('[data-product-json]').textContent).variants;
      this.idInput = this.form.querySelector('[data-variant-id]');
      this.addBtn = this.form.querySelector('[data-add-button]');
      this.msg = this.form.querySelector('[data-product-msg]');
      this.priceEl = this.querySelector('[data-price]');
      this.gallery = this.querySelector('[data-gallery]');
      this.count = this.querySelector('[data-gallery-count]');
      this.buyNow = this.form.querySelector('[data-buy-now]');
      this.sticky = this.querySelector('[data-sticky-atc]');
      this.complete = !this.idInput.hasAttribute('data-incomplete');

      this.form.addEventListener('change', (e) => { if (e.target.matches('[data-option]')) this.onOptionChange(e.target); });
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.add(); });
      this.form.addEventListener('click', (e) => {
        const step = e.target.closest('[data-qty]');
        if (step) this.stepQty(Number(step.dataset.qty));
        if (e.target.closest('[data-buy-lock]')) this.requireChoice();
      });
      this.gallery?.addEventListener('scroll', () => this.updateCount(), { passive: true });
      this.updateGallery();
      this.updateAvailability();
      this.initSticky();
    }

    /* ---------- Options ---------- */
    selectedOptions() {
      const groups = new Map();
      this.form.querySelectorAll('[data-option]').forEach((input) => {
        const pos = Number(input.dataset.option);
        if (!groups.has(pos)) groups.set(pos, null);
        if (input.type !== 'radio' || input.checked) groups.set(pos, input.value);
      });
      return [...groups.entries()].sort((a, b) => a[0] - b[0]).map(([, v]) => v);
    }

    onOptionChange(input) {
      this.msg.textContent = '';
      const group = input.closest('[data-option-group]');
      const colorName = group?.querySelector('[data-color-name]');
      if (colorName) colorName.textContent = input.value;
      const options = this.selectedOptions();
      this.complete = options.every((v) => v !== null);
      const variant = this.complete ? this.variants.find((v) => v.options.every((o, i) => o === options[i])) : null;
      if (variant) {
        this.idInput.value = variant.id;
        this.idInput.removeAttribute('data-incomplete');
        this.priceEl.textContent = variant.price;
        this.querySelector('[data-sticky-price]').textContent = variant.price;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
      this.setAddState(variant);
      this.buyNow?.classList.toggle('is-locked', !this.complete);
      if (this.colorPosition() === Number(input.dataset.option)) this.updateGallery();
      this.updateAvailability();
      this.syncStickySize();
    }

    setAddState(variant) {
      const soldOut = this.complete && (!variant || !variant.available);
      this.addBtn.disabled = soldOut;
      this.addBtn.textContent = soldOut ? this.addBtn.dataset.labelSoldOut : this.addBtn.dataset.labelAdd;
    }

    colorPosition() {
      const swatch = this.form.querySelector('.cswatch [data-option]');
      return swatch ? Number(swatch.dataset.option) : null;
    }

    // Grey out option values with no available variant for the other current choices.
    updateAvailability() {
      const options = this.selectedOptions();
      this.form.querySelectorAll('input[type="radio"][data-option]').forEach((input) => {
        const idx = Number(input.dataset.option) - 1;
        const available = this.variants.some((v) => v.available && v.options[idx] === input.value
          && v.options.every((o, i) => i === idx || options[i] === null || o === options[i]));
        input.nextElementSibling?.classList.toggle('is-unavailable', !available);
      });
    }

    /* ---------- Gallery ---------- */
    updateGallery() {
      if (!this.gallery) return;
      const pos = this.colorPosition();
      const color = pos ? this.handleize(this.selectedOptions()[pos - 1] || '') : '';
      const figures = [...this.gallery.querySelectorAll('figure')];
      const hasColorImages = color && figures.some((f) => f.dataset.color === color);
      figures.forEach((f) => { f.hidden = Boolean(hasColorImages && f.dataset.color && f.dataset.color !== color); });
      // Bring the selected color's photos to the front.
      if (hasColorImages) figures.filter((f) => f.dataset.color === color).reverse().forEach((f) => this.gallery.prepend(f));
      this.gallery.scrollTo({ left: 0 });
      this.updateCount();
      const first = figures.find((f) => !f.hidden && f.dataset.color === color)?.querySelector('img');
      const stickyImg = this.querySelector('[data-sticky-image]');
      if (first && stickyImg) { stickyImg.srcset = ''; stickyImg.src = first.currentSrc || first.src; }
    }

    updateCount() {
      if (!this.count || !this.gallery) return;
      const visible = [...this.gallery.querySelectorAll('figure')].filter((f) => !f.hidden);
      const index = Math.round(this.gallery.scrollLeft / (this.gallery.clientWidth || 1)) + 1;
      this.count.textContent = `${Math.min(index, visible.length)} / ${visible.length}`;
      this.count.hidden = visible.length < 2;
    }

    handleize(s) { return s.toLowerCase().replace(/['’]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''); }

    /* ---------- Quantity + add ---------- */
    stepQty(delta) {
      const input = this.form.querySelector('[data-qty-input]');
      input.value = Math.max(1, (Number(input.value) || 1) + delta);
    }

    requireChoice() {
      this.msg.textContent = this.msg.dataset.msgChoose;
      const groups = [...this.form.querySelectorAll('[data-option-group]')];
      const missing = groups.find((g) => !g.querySelector('input:checked'));
      missing?.querySelector('input')?.focus();
      return false;
    }

    async add() {
      if (!this.complete) return this.requireChoice();
      this.addBtn.setAttribute('aria-busy', 'true');
      this.addBtn.disabled = true;
      try {
        const res = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(this.form),
        });
        const data = await res.json();
        if (!res.ok) { this.msg.textContent = data.description || data.message || ''; return; }
        const cart = await window.AmenCart.refreshCount();
        const added = new CustomEvent('cart:added', { detail: { cart, trigger: this.addBtn }, cancelable: true });
        if (document.dispatchEvent(added)) window.location.href = routes.cart_url || '/cart';
      } catch (error) {
        this.form.submit(); // network or script problem: fall back to a normal form post
      } finally {
        this.addBtn.removeAttribute('aria-busy');
        this.setAddState(this.variants.find((v) => String(v.id) === this.idInput.value));
      }
    }

    /* ---------- Sticky add to cart ---------- */
    initSticky() {
      if (!this.sticky) return;
      const buyRow = this.form.querySelector('[data-buyrow]');
      this.stickySelect = this.sticky.querySelector('[data-sticky-size]');
      this.stickySelect?.addEventListener('change', () => {
        const radio = [...this.form.querySelectorAll(`input[data-option="${this.stickySelect.dataset.stickySize}"]`)]
          .find((r) => r.value === this.stickySelect.value);
        if (radio) { radio.checked = true; this.onOptionChange(radio); }
      });
      this.sticky.querySelector('[data-sticky-add]').addEventListener('click', () => {
        if (!this.complete) { this.requireChoice(); this.stickySelect?.focus(); return; }
        this.add();
      });
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.sticky.classList.toggle('is-on', show);
        this.sticky.setAttribute('aria-hidden', String(!show));
        this.sticky.toggleAttribute('inert', !show);
      }).observe(buyRow);
    }

    syncStickySize() {
      if (!this.stickySelect) return;
      const options = this.selectedOptions();
      this.stickySelect.value = options[Number(this.stickySelect.dataset.stickySize) - 1] || '';
    }
  }

  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* <complete-look>: checkbox list with a running total; adds the selected items in one request. */
  if (!customElements.get('complete-look')) customElements.define('complete-look', class extends HTMLElement {
      connectedCallback() {
        this.boxes = [...this.querySelectorAll('input[type="checkbox"]')];
        this.button = this.querySelector('[data-add-selected]');
        this.total = this.querySelector('[data-total]');
        this.format = this.querySelector('[data-money-format]').textContent;
        this.msg = this.querySelector('[data-ctl-msg]');
        this.addEventListener('change', (event) => {
          const select = event.target.closest('[data-ctl-variant]');
          if (select) {
            // Picking a size also ticks the item and sets its real price.
            const box = select.closest('.ctl__item').querySelector('input[type="checkbox"]');
            const option = select.selectedOptions[0];
            box.dataset.variantId = select.value;
            if (option && option.dataset.price) box.dataset.price = option.dataset.price;
            if (select.value) { box.checked = true; select.removeAttribute('aria-invalid'); }
          }
          this.update();
        });
        this.button.addEventListener('click', () => this.add());
        this.update();
      }
      money(cents) {
        const value = (cents / 100).toFixed(2);
        return this.format.replace(/\{\{\s*\w+\s*\}\}/, value);
      }
      update() {
        const sel = this.boxes.filter((b) => b.checked);
        this.total.textContent = this.money(sel.reduce((s, b) => s + Number(b.dataset.price), 0));
        this.button.disabled = !sel.length;
        this.button.textContent = !sel.length ? this.dataset.labelNone
          : sel.length === 1 ? this.dataset.labelAddOne : this.dataset.labelAddMany.replace('[count]', sel.length);
      }
      async add() {
        const checked = this.boxes.filter((b) => b.checked);
        const missing = checked.map((b) => b.closest('.ctl__item').querySelector('[data-ctl-variant]')).filter((s) => s && !s.value);
        if (missing.length) {
          missing.forEach((s) => s.setAttribute('aria-invalid', 'true'));
          if (this.msg) this.msg.textContent = this.msg.dataset.msgChoose;
          missing[0].focus();
          return;
        }
        if (this.msg) this.msg.textContent = '';
        const items = checked.map((b) => ({ id: Number(b.dataset.variantId), quantity: 1 }));
        if (!items.length) return;
        this.button.setAttribute('aria-busy', 'true');
        const routes = window.routes || {};
        const res = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }),
        });
        this.button.removeAttribute('aria-busy');
        if (!res.ok) return;
        const cart = await window.AmenCart.refreshCount();
        const added = new CustomEvent('cart:added', { detail: { cart, trigger: this.button }, cancelable: true });
        if (document.dispatchEvent(added)) window.location.href = routes.cart_url || '/cart';
      }
  });

  /* <product-recommendations>: swap in the Product Recommendations API render (family first, then related). */
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', class extends HTMLElement {
    connectedCallback() {
      new IntersectionObserver((entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        fetch(this.dataset.url).then((r) => r.text()).then((html) => {
          const next = new DOMParser().parseFromString(html, 'text/html').querySelector('product-recommendations .row');
          const row = this.querySelector('.row');
          if (next && row && next.children.length) row.innerHTML = next.innerHTML;
        }).catch(() => { /* keep the server-rendered cards */ });
      }, { rootMargin: '400px 0px' }).observe(this);
    }
  });
})();
